import { StatusChip, type ChipTone } from '@/design';

/**
 * The Email log's vocabularies in words (SR-523 stage 3) — kinds, statuses, why a mail was
 * skipped, what the provider reported, where a suppression came from. Words only: a value
 * survey-api adds later is drawn as spelled.
 */

interface IWord {
  label: string;
  tone: ChipTone;
}

const of = (table: Record<string, IWord>, value: string): IWord => table[value] ?? { label: value, tone: 'neutral' };

export const MAIL_KINDS: Record<string, string> = {
  outreach: 'Outreach',
  cycle_reminder: 'Cycle reminder',
  cycle_invitation: 'Cycle invitation',
  submit_confirmation: 'Submission confirmation',
  template_test: 'Template test'
};

export const kindLabel = (kind: string): string => MAIL_KINDS[kind] ?? kind;

export const MAIL_STATUSES: Record<string, IWord> = {
  queued: { label: 'Queued', tone: 'info' },
  sending: { label: 'Sending', tone: 'info' },
  sent: { label: 'Sent', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  skipped: { label: 'Skipped', tone: 'neutral' }
};

/** The words for why a mail was skipped or failed; an excluded participant's names its reason. */
export function reasonWords(reason: string): string {
  if (reason.startsWith(EXCLUDED_PREFIX)) return `excluded: ${reason.slice(EXCLUDED_PREFIX.length)}`;
  return MAIL_REASONS[reason] ?? reason;
}

const EXCLUDED_PREFIX = 'excluded:';

export const MAIL_REASONS: Record<string, string> = {
  suppressed: 'unsubscribed',
  no_address: 'no address',
  withheld: 'withheld on this environment',
  rejected: 'refused by the provider',
  render: 'could not be rendered',
  cancelled: 'campaign cancelled',
  transient: 'gave up after retries'
};

export const MAIL_DELIVERY: Record<string, IWord> = {
  hard_bounce: { label: 'Bounced', tone: 'danger' },
  soft_bounce: { label: 'Soft bounce', tone: 'attention' },
  blocked: { label: 'Blocked', tone: 'danger' },
  spam: { label: 'Marked as spam', tone: 'danger' },
  unsub: { label: 'Unsubscribed', tone: 'attention' }
};

export const CAMPAIGN_STATUSES: Record<string, IWord> = {
  draft: { label: 'Draft', tone: 'neutral' },
  queued: { label: 'Queued', tone: 'info' },
  sending: { label: 'Sending', tone: 'info' },
  done: { label: 'Done', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'neutral' }
};

export const CAMPAIGN_KINDS: Record<string, string> = {
  outreach: 'Outreach',
  reminder: 'Reminder',
  invitation: 'Invitation'
};

export const SUPPRESSION_SOURCES: Record<string, string> = {
  link: 'Unsubscribe link',
  one_click: 'One-click unsubscribe',
  provider_unsub: 'Unsubscribed at the provider',
  complaint: 'Spam complaint',
  staff: 'Added by staff'
};

export const sourceLabel = (source: string): string => SUPPRESSION_SOURCES[source] ?? source;

export function MailStatusChip({ status, reason }: { status: string; reason?: Nullable<string> }) {
  const word = of(MAIL_STATUSES, status);
  return (
    <span className="grid justify-items-start gap-0.5" data-mail-status={status}>
      <StatusChip tone={word.tone}>{word.label}</StatusChip>
      {reason && <span className="text-xs text-muted-foreground">{reasonWords(reason)}</span>}
    </span>
  );
}

export function DeliveryChip({ delivery }: { delivery: Nullable<string> }) {
  if (!delivery) return null;
  const word = of(MAIL_DELIVERY, delivery);
  return (
    <span data-mail-delivery={delivery}>
      <StatusChip tone={word.tone}>{word.label}</StatusChip>
    </span>
  );
}

export function CampaignStatusChip({ status }: { status: string }) {
  const word = of(CAMPAIGN_STATUSES, status);
  return (
    <span data-campaign-status={status}>
      <StatusChip tone={word.tone}>{word.label}</StatusChip>
    </span>
  );
}

/** "FA · v2", "All surveys · v1", or nothing. */
export function variantWords(survey: Nullable<string>, version: Nullable<number>): string {
  const which = survey ?? 'All surveys';
  return version === null ? which : `${which} · v${version}`;
}

export const vocabulary = (table: Record<string, string | IWord>) =>
  Object.entries(table).map(([value, word]) => ({ value, label: typeof word === 'string' ? word : word.label }));
